<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import { formatedDate, generateUniqueId } from '@/constants'
import Dropdown from 'primevue/dropdown'
import Calendar from 'primevue/calendar'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import { Icon } from '@iconify/vue/dist/iconify.js'
import type { Customer, LinkedProduct } from '@/types/customer-types'
import { useRouter } from 'vue-router'
import {
  addLinkedProductToCustomer,
  editLinkedProductForCustomer,
  deleteLinkedProductForCustomer,
  getCustomerById
} from '@/api/customers'
import { useCustomerForm } from '@/composables/useCustomerForm'
import { FabricStatusEnum, type Product } from '@/types/product-types'
import { getProductList } from '@/api/products'
import { currencyOptions } from '@/constants/currency'
import { priceTypeOptions } from '@/constants/customers'

const router = useRouter()
const { loading, showToast } = useCustomerForm('view')
const editingRow = ref(null)
const customer = ref<Customer>()
const linkedProducts = ref<LinkedProduct[]>([])
const qualities = ref<Product[] | undefined>([])
const searchQuery = ref('')
const customerId = router.currentRoute.value.params.id as string
const isAddingNew = ref<boolean>(false)

const isEditingRow = (rowId: string) => editingRow.value === rowId

const getQuality = (qualityId: string) => {
  return qualities.value?.find((quality) => quality.id === qualityId)?.finishedReference
}

const addLinkedProduct = () => {
  const id = generateUniqueId()

  const newRow = {
    id,
    qualityId: '',
    priceType: 0,
    costPerUnitAmount: 0,
    costPerUnitCurrency: 0,
    customerQualityReference: '',
    startDate: new Date(),
    isNew: true
  }

  linkedProducts.value.push({ ...newRow })
  handleEnableEdit(id)
  isAddingNew.value = true
}

const handleEnableEdit = (id: any) => {
  editingRow.value = id
}

const handleSave = async (index: number, id: string) => {
  const item = linkedProducts.value.find((item) => item.id === id)
  if (!item) {
    return
  }

  const linkedProduct: LinkedProduct = {
    qualityId: item.qualityId,
    priceType: item.priceType,
    costPerUnitAmount: item.costPerUnitAmount,
    costPerUnitCurrency: item.costPerUnitCurrency,
    customerQualityReference: item.customerQualityReference,
    startDate: new Date(item.startDate).toISOString()
  }

  loading.value = true
  try {
    await addLinkedProductToCustomer(customerId, linkedProduct)
    showToast('success', 'Linked product added successfully')
    
    editingRow.value = null
    isAddingNew.value = false
    
    customer.value = await getCustomerById(customerId)
    linkedProducts.value =
      customer?.value?.linkedProducts.map((item) => ({
        ...item,
        startDate: item.startDate ? new Date(item.startDate.toString()) : ''
      })) || []
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors
        .map((err: { code: string; description: string }) => err.description)
        .join(', ')
      showToast('error', 'Validation Error', errorMessages)
    }
    else if (error.response) {
      showToast('error', 'Error', `Server Error: ${error.response.status} - ${error.response.data.title || error.message}`)
    }
    else if (error.request) {
      showToast('error', 'Error', 'No response from the server.')
    }
    else {
      showToast('error', 'Error', `Request setup error: ${error.message}`)
    }
  } finally {
    loading.value = false
  }
}

const handleUpdate = async (index: number, id: string) => {
  const item = linkedProducts.value.find((item) => item.id === id)
  if (!item) {
    return
  }

  const linkedProduct: LinkedProduct = {
    id: item.id,
    qualityId: item.qualityId,
    priceType: item.priceType,
    costPerUnitAmount: item.costPerUnitAmount,
    costPerUnitCurrency: item.costPerUnitCurrency,
    customerQualityReference: item.customerQualityReference,
    startDate: item.startDate
  }

  loading.value = true
  try {
    await editLinkedProductForCustomer(customerId, linkedProduct, linkedProduct.id)
    showToast('success', 'Linked product updated successfully')
    
    editingRow.value = null
  } catch (error: any) {
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors
        .map((err: { code: string; description: string }) => err.description)
        .join(', ')
      showToast('error', 'Validation Error', errorMessages)
    }
    else if (error.response) {
      showToast('error', 'Error', `Server Error: ${error.response.status} - ${error.response.data.title || error.message}`)
    }
    else if (error.request) {
      showToast('error', 'Error', 'No response from the server.')
    }
    else {
      showToast('error', 'Error', `Request setup error: ${error.message}`)
    }
  } finally {
    loading.value = false
  }
}

const handleDelete = async (linkedProductId: string) => {
  loading.value = true
  try {
    await deleteLinkedProductForCustomer(customerId, linkedProductId)
    showToast('success', 'Linked product deleted successfully')

    customer.value = await getCustomerById(customerId)
    linkedProducts.value =
      customer?.value?.linkedProducts.map((item) => ({
        ...item,
        startDate: item.startDate ? new Date(item.startDate.toString()) : ''
      })) || []
  } catch (error: any) {
    console.error(error)
    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors
        .map((err: { code: string; description: string }) => err.description)
        .join(', ')
      showToast('error', 'Validation Error', errorMessages)
    }
    else if (error.response) {
      showToast('error', 'Error', `Server Error: ${error.response.status} - ${error.response.data.title || error.message}`)
    }
    else if (error.request) {
      showToast('error', 'Error', 'No response from the server.')
    }
    else {
      showToast('error', 'Error', `Request setup error: ${error.message}`)
    }
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  loading.value = true
  const allProducts = await getProductList()
  qualities.value = allProducts.filter(
    (product) => product.fabricStatus === FabricStatusEnum.Finished
  )
  customer.value = await getCustomerById(customerId)
  linkedProducts.value =
    customer?.value?.linkedProducts.map((item) => ({
      ...item,
      startDate: item.startDate ? new Date(item.startDate.toString()) : ''
    })) || []
  loading.value = false
})

const filteredLinkedProducts = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return linkedProducts.value.filter(
    (linkedProduct: LinkedProduct) =>
      getQuality(linkedProduct.qualityId)?.toLowerCase().includes(query) ||
      linkedProduct.priceType?.toString().includes(query) ||
      linkedProduct.costPerUnitAmount?.toString().includes(query) ||
      linkedProduct.customerQualityReference?.toLowerCase().includes(query) ||
      formatedDate(linkedProduct.startDate.toString()).toLowerCase().includes(query)
  )
})
</script>

<template>
  <DataTable
    :value="filteredLinkedProducts"
    paginator
    :rows="10"
    tableStyle="min-width: 70rem"
    class="overflow-hidden"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}  linked products"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
  >
    <template #header>
      <div class="flex flex-wrap items-center justify-between pb-3 gap-2">
        <div>
          <h2 class="text-xl font-semibold tracking-tight">Linked Products</h2>
          <p class="text-neutral-500 font-light text-sm">
            List of products linked to this customer
          </p>
        </div>
        <TableSearchInput class="w-full md:w-auto" v-model="searchQuery" />
      </div>
    </template>

    <Column
        field="qualityId"
        header="Quality"
        sortable
        :style="{ minWidth: '100px' }"
        :breakpoints="{ '960px': 'hidden' }"
    >
      <template #body="slotProps">
        <RouterLink
            v-if="!isEditingRow(slotProps.data.id) && slotProps.data.qualityId"
            :to="`/productSpecification/${slotProps.data.qualityId}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getQuality(slotProps.data.qualityId) }}
        </RouterLink>

        <Dropdown
            v-else
            inputName="qualityId"
            id="qualityId"
            placeholder="Choose Quality"
            :options="qualities"
            optionLabel="finishedReference"
            optionValue="id"
            class="w-full"
            :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
            :disabled="!isEditingRow(slotProps.data.id)"
            v-model="slotProps.data.qualityId"
        />
      </template>
    </Column>

    <Column field="priceTypes" sortable header="Price Type" :style="{ minWidth: '100px' }">
      <template #body="slotProps">
        <Dropdown
          :options="priceTypeOptions"
          placeholder="Select price type"
          optionLabel="label"
          optionValue="value"
          class="w-1/7"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          v-model="slotProps.data.priceType"
        />
      </template>
    </Column>

    <Column field="costPerUnit" sortable header="Cost Per Unit" :style="{ minWidth: '10rem' }">
      <template #body="slotProps">
        <span v-if="!isEditingRow(slotProps.data.id)">
        {{ currencyOptions.find(c => c.value === slotProps.data.costPerUnitCurrency)?.label }} {{ slotProps.data.costPerUnitAmount }}
    </span>

        <div v-else class="flex items-center gap-x-2 w-full">
          <Dropdown
            :options="currencyOptions"
            optionLabel="label"
            optionValue="value"
            class="w-1/3"
          v-model="slotProps.data.costPerUnitCurrency"
          />
          <InputNumber
            v-model="slotProps.data.costPerUnitAmount"
            class="w-2/3 overflow-hidden"
          placeholder="Enter cost"
          :min-fraction-digits="0"
          :max-fraction-digits="5"
          />
        </div>
      </template>
    </Column>
    <Column
      field="customerQualityReference"
      sortable
      header="Customer Quality Reference"
      :style="{ minWidth: '100px' }"
      :breakpoints="{ '768px': 'hidden' }"
    >
      <template #body="slotProps">
        <InputText
          v-model="slotProps.data.customerQualityReference"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          placeholder="Enter customer quality reference"
          class="w-full"
        />
      </template>
    </Column>

    <Column field="startDate" sortable header="Start Date" :style="{ minWidth: '10rem' }">
      <template #body="slotProps">
        <p v-if="!isEditingRow(slotProps.data.id)" class="px-4">
          {{ formatedDate(slotProps.data.startDate) }}
        </p>
        <Calendar
          v-else
          v-model="slotProps.data.startDate"
          placeholder="Start date"
          class="!w-32 overflow-hidden"
          dateFormat="dd/mm/yy"
        />
      </template>
    </Column>
    <Column field="" header="">
      <template #body="slotProps" >
        <Button
          v-ripple
          outlined
          class="gap-x-2 w-1/8"
          @click="handleEnableEdit(slotProps.data.id)"
          :loading="loading"
          :disabled="false"
          v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew"
        >
          <span>Edit</span>
          <Icon icon="basil:edit-outline" class="text-2xl" />
        </Button>
        <div v-else class="flex gap-2">
          <Button
            v-ripple
            outlined
            class="w-1/8"
            @click="handleSave(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-if="slotProps.data.isNew"
          >
            <span>Save</span>
            <Icon icon="basil:check-outline" class="text-xl" />
          </Button>
          <Button
            v-ripple
            outlined
            class="w-1/8"
            @click="handleUpdate(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-else
          >
            <span>Update</span>
            <Icon icon="basil:refresh-outline" class="text-xl" />
          </Button>
        </div>
      </template>
    </Column>
    <Column field="" header="">
      <template #body="slotProps">
        <div class="relative inline-block group/tooltip" v-if="!slotProps.data.isNew">
          <Button
            v-ripple
            outlined
            severity="danger"
            class="gap-x-2 w-1/8"
            @click="handleDelete(slotProps.data.id)"
            :loading="loading"
            :disabled="!slotProps.data.canBeDeleted"
            v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew"
          >
            <span>Delete</span>
            <Icon icon="basil:trash-outline" class="text-2xl" />
          </Button>

          <div
            v-if="!slotProps.data.canBeDeleted"
            class="absolute right-full top-1/2 transform -translate-y-1/2 -translate-x-2 opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none z-50"
          >
            <div class="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap max-w-xs">
              Cannot delete linked product that has been used by orders
            </div>
            <div class="absolute top-1/2 right-0 transform translate-x-full -translate-y-1/2 w-0 h-0 border-l-4 border-r-0 border-t-4 border-b-4 border-transparent border-l-gray-800"></div>
          </div>
        </div>
      </template>
    </Column>
    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>

  <div class="px-4 py-2">
    <Button
      v-ripple
      outlined
      class="gap-x-2 w-full sm:w-auto"
      @click="addLinkedProduct()"
      :loading="loading"
      :disabled="isAddingNew"
    >
      <span>Add New Linked Product</span>
      <Icon icon="basil:plus-solid" class="text-2xl" />
    </Button>
  </div>
</template>
<style scoped>
.p-inputtext {
  max-width: 100%;
  overflow: hidden;
}
.p-dropdown {
  max-width: 100%;
  overflow: hidden;
}
.p-calendar {
  max-width: 100%;
  overflow: hidden;
}
</style>
